import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import QRCode from 'qrcode';

import { APP_ENV } from '../config/app-environment';
import {
  AccessCode,
  AppUser,
  BulkJob,
  BulkUploadDraft,
  ControllerVendor,
  ControllerVendorDraft,
  DashboardSnapshot,
  GroupAccessCode,
  GroupCodeDraft,
  HospitalityEvent,
  HospitalityEventDraft,
  Location,
  ManualCodeDraft,
  PmsVendor,
  PmsVendorDraft,
  Space,
  Tenant,
  TenantDraft
} from '../models/hospitality';
import { ApiResponse, DeletedEntityData } from '../models/api-response';
import {
  createControllerVendorRequestSchema,
  bulkCreateLocationsRequestSchema,
  bulkCreateUnitsRequestSchema,
  createGroupCodeRequestSchema,
  createEventRequestSchema,
  createLocationRequestSchema,
  createManualCodeRequestSchema,
  createPmsVendorRequestSchema,
  createUnitRequestSchema,
  createUserRequestSchema,
  CreateLocationRequest,
  CreateControllerVendorRequest,
  CreatePmsVendorRequest,
  CreateUserRequest,
  CreateUnitRequest,
  BulkCreateLocationsRequest,
  BulkCreateUnitsRequest,
  updateControllerVendorRequestSchema,
  updateEventRequestSchema,
  updatePmsVendorRequestSchema,
  updateUnitRequestSchema,
  updateUserRequestSchema,
  UpdateLocationRequest,
  UpdateControllerVendorRequest,
  UpdatePmsVendorRequest,
  UpdateUnitRequest,
  UpdateUserRequest
} from '../models/dashboard-api';
import {
  ActiveCodesQuery,
  ArchivedCodesQuery,
  BulkJobsQuery,
  ControllerVendorsQuery,
  EventNetworksQuery,
  EventsQuery,
  LocationsQuery,
  PagedControllerVendorResult,
  PagedEventNetworkResult,
  PagedEventResult,
  PagedAccessCodeResult,
  PagedBulkJobResult,
  PagedLocationResult,
  PagedPmsVendorResult,
  PagedSpaceResult,
  PagedTenantResult,
  PagedUserResult,
  PmsVendorsQuery,
  SpaceListQuery,
  TenantsQuery,
  UsersQuery
} from '../models/hospitality-tables';
import { ApiActivityService } from './api-activity.service';
import { HospitalityApiService } from './hospitality-api.service';

@Injectable({ providedIn: 'root' })
export class HttpHospitalityApiService extends HospitalityApiService {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(APP_ENV);
  private readonly activity = inject(ApiActivityService);

  override async loadDashboardSnapshot(tenantId: string): Promise<ApiResponse<DashboardSnapshot>> {
    return this.get(`/tenants/${encodeURIComponent(tenantId)}/dashboard/bootstrap`);
  }

  override async listLocationSpaces(query: SpaceListQuery): Promise<ApiResponse<PagedSpaceResult>> {
    return this.get(
      `/tenants/${encodeURIComponent(query.tenantId)}/locations/${encodeURIComponent(query.locationId)}/spaces`,
      this.toHttpParams({
        segment: query.segment,
        page: query.page,
        pageSize: query.pageSize,
        sortKey: query.sortKey,
        sortDirection: query.sortDirection
      })
    );
  }

  override async getActiveSpaceCode(
    tenantId: string,
    locationId: string,
    spaceId: string
  ): Promise<ApiResponse<AccessCode>> {
    return this.get(
      `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/spaces/${encodeURIComponent(spaceId)}/codes/active`
    );
  }

  override async getActiveSpaceNetworkQr(
    tenantId: string,
    locationId: string,
    spaceId: string
  ): Promise<ApiResponse<{ ssid: string; passphrase: string; qrPayload: string }>> {
    return this.get(
      `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/spaces/${encodeURIComponent(spaceId)}/codes/active/network-qr`
    );
  }

  override async listActiveLocationCodes(query: ActiveCodesQuery): Promise<ApiResponse<PagedAccessCodeResult>> {
    return this.get(
      `/tenants/${encodeURIComponent(query.tenantId)}/locations/${encodeURIComponent(query.locationId)}/codes/active`,
      this.toHttpParams({
        sourceFilter: query.sourceFilter,
        codeType: query.codeType,
        search: query.search,
        page: query.page,
        pageSize: query.pageSize,
        sortKey: query.sortKey,
        sortDirection: query.sortDirection
      })
    );
  }

  override async listArchivedLocationCodes(
    query: ArchivedCodesQuery
  ): Promise<ApiResponse<PagedAccessCodeResult>> {
    return this.get(
      `/tenants/${encodeURIComponent(query.tenantId)}/locations/${encodeURIComponent(query.locationId)}/codes/archived`,
      this.toHttpParams({
        search: query.search,
        codeType: query.codeType,
        page: query.page,
        pageSize: query.pageSize,
        sortKey: query.sortKey,
        sortDirection: query.sortDirection
      })
    );
  }

  override async listLocationEvents(query: EventsQuery): Promise<ApiResponse<PagedEventResult>> {
    return this.get(
      `/tenants/${encodeURIComponent(query.tenantId)}/locations/${encodeURIComponent(query.locationId)}/events`,
      this.toHttpParams({
        status: query.status,
        search: query.search,
        page: query.page,
        pageSize: query.pageSize,
        sortKey: query.sortKey,
        sortDirection: query.sortDirection
      })
    );
  }

  override async listLocationEventNetworks(
    query: EventNetworksQuery
  ): Promise<ApiResponse<PagedEventNetworkResult>> {
    return this.get(
      `/tenants/${encodeURIComponent(query.tenantId)}/locations/${encodeURIComponent(query.locationId)}/networks`,
      this.toHttpParams({
        page: query.page,
        pageSize: query.pageSize
      })
    );
  }

  override async listLocationBulkJobs(query: BulkJobsQuery): Promise<ApiResponse<PagedBulkJobResult>> {
    return this.get(
      `/locations/${encodeURIComponent(query.locationId)}/bulk-jobs`,
      this.toHttpParams({
        page: query.page,
        pageSize: query.pageSize,
        sortKey: query.sortKey,
        sortDirection: query.sortDirection
      })
    );
  }

  override async listTenants(query: TenantsQuery): Promise<ApiResponse<PagedTenantResult>> {
    return this.get(
      '/tenants',
      this.toHttpParams({
        page: query.page,
        pageSize: query.pageSize,
        sortKey: query.sortKey,
        sortDirection: query.sortDirection
      })
    );
  }

  override async listUsers(query: UsersQuery): Promise<ApiResponse<PagedUserResult>> {
    return this.get(
      '/users',
      this.toHttpParams({
        page: query.page,
        pageSize: query.pageSize,
        sortKey: query.sortKey,
        sortDirection: query.sortDirection
      })
    );
  }

  override async listLocations(query: LocationsQuery): Promise<ApiResponse<PagedLocationResult>> {
    return this.get(
      `/tenants/${encodeURIComponent(query.tenantId)}/locations`,
      this.toHttpParams({
        page: query.page,
        pageSize: query.pageSize,
        sortKey: query.sortKey,
        sortDirection: query.sortDirection
      })
    );
  }

  override async listControllerVendors(
    query: ControllerVendorsQuery
  ): Promise<ApiResponse<PagedControllerVendorResult>> {
    return this.get(
      '/controller-vendors',
      this.toHttpParams({
        page: query.page,
        pageSize: query.pageSize,
        sortKey: query.sortKey,
        sortDirection: query.sortDirection
      })
    );
  }

  override async listPmsVendors(query: PmsVendorsQuery): Promise<ApiResponse<PagedPmsVendorResult>> {
    return this.get(
      '/pms-vendors',
      this.toHttpParams({
        page: query.page,
        pageSize: query.pageSize,
        sortKey: query.sortKey,
        sortDirection: query.sortDirection
      })
    );
  }

  override async listControllerVendorOptions(): Promise<ApiResponse<ControllerVendor[]>> {
    return this.get('/controller-vendors/options');
  }

  override async listPmsVendorOptions(): Promise<ApiResponse<PmsVendor[]>> {
    return this.get('/pms-vendors/options');
  }

  override async getControllerVendor(vendorId: string): Promise<ApiResponse<ControllerVendor>> {
    return this.get(`/controller-vendors/${encodeURIComponent(vendorId)}`);
  }

  override async getPmsVendor(vendorId: string): Promise<ApiResponse<PmsVendor>> {
    return this.get(`/pms-vendors/${encodeURIComponent(vendorId)}`);
  }

  override async createTenant(draft: TenantDraft): Promise<ApiResponse<Tenant>> {
    return this.post('/tenants', this.buildTenantPayload(draft));
  }

  override async updateTenant(tenantId: string, draft: TenantDraft): Promise<ApiResponse<Tenant>> {
    return this.put(`/tenants/${encodeURIComponent(tenantId)}`, this.buildTenantPayload(draft));
  }

  override async deleteTenant(tenantId: string): Promise<ApiResponse<DeletedEntityData>> {
    return this.delete(`/tenants/${encodeURIComponent(tenantId)}`);
  }

  override async createControllerVendor(
    draft: ControllerVendorDraft
  ): Promise<ApiResponse<ControllerVendor>> {
    return this.post('/controller-vendors', this.buildControllerVendorPayload(draft));
  }

  override async updateControllerVendor(
    vendorId: string,
    draft: UpdateControllerVendorRequest | ControllerVendorDraft
  ): Promise<ApiResponse<ControllerVendor>> {
    return this.put(
      `/controller-vendors/${encodeURIComponent(vendorId)}`,
      this.buildControllerVendorPayload(draft)
    );
  }

  override async deleteControllerVendor(vendorId: string): Promise<ApiResponse<DeletedEntityData>> {
    return this.delete(`/controller-vendors/${encodeURIComponent(vendorId)}`);
  }

  override async createPmsVendor(draft: PmsVendorDraft): Promise<ApiResponse<PmsVendor>> {
    return this.post('/pms-vendors', this.buildPmsVendorPayload(draft));
  }

  override async updatePmsVendor(
    vendorId: string,
    draft: UpdatePmsVendorRequest | PmsVendorDraft
  ): Promise<ApiResponse<PmsVendor>> {
    return this.put(`/pms-vendors/${encodeURIComponent(vendorId)}`, this.buildPmsVendorPayload(draft));
  }

  override async deletePmsVendor(vendorId: string): Promise<ApiResponse<DeletedEntityData>> {
    return this.delete(`/pms-vendors/${encodeURIComponent(vendorId)}`);
  }

  override async createLocation(
    tenantId: string,
    draft: CreateLocationRequest
  ): Promise<ApiResponse<Location>> {
    return this.post(`/tenants/${encodeURIComponent(tenantId)}/locations`, this.buildLocationPayload(draft));
  }

  override async updateLocation(
    tenantId: string,
    locationId: string,
    draft: UpdateLocationRequest
  ): Promise<ApiResponse<Location>> {
    return this.put(
      `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}`,
      this.buildLocationPayload(draft)
    );
  }

  override async deleteLocation(
    tenantId: string,
    locationId: string
  ): Promise<ApiResponse<DeletedEntityData>> {
    return this.delete(
      `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}`
    );
  }

  override async bulkCreateLocations(
    tenantId: string,
    request: BulkCreateLocationsRequest
  ): Promise<ApiResponse<{ created: number; failed: number; items: Location[] }>> {
    return this.post(
      `/tenants/${encodeURIComponent(tenantId)}/locations/bulk`,
      bulkCreateLocationsRequestSchema.parse(request)
    );
  }

  override async createUser(draft: CreateUserRequest): Promise<ApiResponse<AppUser>> {
    return this.post('/users', createUserRequestSchema.parse(draft));
  }

  override async updateUser(userId: string, draft: UpdateUserRequest): Promise<ApiResponse<AppUser>> {
    return this.put(`/users/${encodeURIComponent(userId)}`, updateUserRequestSchema.parse(draft));
  }

  override async deleteUser(userId: string): Promise<ApiResponse<DeletedEntityData>> {
    return this.delete(`/users/${encodeURIComponent(userId)}`);
  }

  override async createUnit(
    tenantId: string,
    locationId: string,
    draft: CreateUnitRequest
  ): Promise<ApiResponse<Space>> {
    return this.post(
      `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/spaces`,
      createUnitRequestSchema.parse(draft)
    );
  }

  override async updateUnit(
    tenantId: string,
    locationId: string,
    spaceId: string,
    draft: UpdateUnitRequest
  ): Promise<ApiResponse<Space>> {
    return this.put(
      `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/spaces/${encodeURIComponent(spaceId)}`,
      updateUnitRequestSchema.parse(draft)
    );
  }

  override async deleteUnit(
    tenantId: string,
    locationId: string,
    spaceId: string
  ): Promise<ApiResponse<DeletedEntityData>> {
    return this.delete(
      `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/spaces/${encodeURIComponent(spaceId)}`
    );
  }

  override async bulkCreateUnits(
    tenantId: string,
    locationId: string,
    request: BulkCreateUnitsRequest
  ): Promise<ApiResponse<{ created: number; failed: number; items: Space[] }>> {
    return this.post(
      `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/spaces/bulk`,
      bulkCreateUnitsRequestSchema.parse(request)
    );
  }

  override async createManualCode(
    tenantId: string,
    locationId: string,
    draft: Omit<ManualCodeDraft, 'locationId'>,
    _spaces: Space[]
  ): Promise<ApiResponse<AccessCode>> {
    return this.post(
      `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/codes/manual`,
      createManualCodeRequestSchema.parse(draft)
    );
  }

  override async extendCode(
    tenantId: string,
    locationId: string,
    codeId: string,
    hours: number
  ): Promise<ApiResponse<AccessCode>> {
    return this.post(
      `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/codes/${encodeURIComponent(codeId)}/extend`,
      { hours }
    );
  }

  override async revokeCode(
    tenantId: string,
    locationId: string,
    codeId: string
  ): Promise<ApiResponse<AccessCode>> {
    return this.post(
      `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/codes/${encodeURIComponent(codeId)}/revoke`,
      null
    );
  }

  override async sendCodeEmail(
    tenantId: string,
    locationId: string,
    codeId: string
  ): Promise<ApiResponse<{ codeId?: string; channel: 'email'; deliveredAt: string }>> {
    return this.post(
      `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/codes/${encodeURIComponent(codeId)}/send-email`,
      null
    );
  }

  override async createEvent(
    tenantId: string,
    locationId: string,
    draft: HospitalityEventDraft
  ): Promise<ApiResponse<HospitalityEvent>> {
    return this.post(
      `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/events`,
      createEventRequestSchema.parse(draft)
    );
  }

  override async updateEvent(
    tenantId: string,
    locationId: string,
    eventId: string,
    draft: HospitalityEventDraft
  ): Promise<ApiResponse<HospitalityEvent>> {
    return this.put(
      `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/events/${encodeURIComponent(eventId)}`,
      updateEventRequestSchema.parse(draft)
    );
  }

  override async disableEvent(
    tenantId: string,
    locationId: string,
    eventId: string
  ): Promise<ApiResponse<HospitalityEvent>> {
    return this.post(
      `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/events/${encodeURIComponent(eventId)}/disable`,
      null
    );
  }

  override async sendEventEmail(
    tenantId: string,
    locationId: string,
    eventId: string
  ): Promise<ApiResponse<{ eventId?: string; channel: 'email'; deliveredAt: string }>> {
    return this.post(
      `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/events/${encodeURIComponent(eventId)}/send-email`,
      null
    );
  }

  override async createGroupCode(
    tenantId: string,
    locationId: string,
    spaceId: string,
    draft: GroupCodeDraft
  ): Promise<ApiResponse<GroupAccessCode>> {
    return this.post(
      `/tenants/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/spaces/${encodeURIComponent(spaceId)}/codes/group`,
      createGroupCodeRequestSchema.parse(draft)
    );
  }

  override async createBulkJob(
    locationId: string,
    draft: Omit<BulkUploadDraft, 'locationId'>,
    _spaces: Space[]
  ): Promise<ApiResponse<{ job: BulkJob; codes: AccessCode[] }>> {
    return this.post(`/locations/${encodeURIComponent(locationId)}/bulk-jobs`, draft);
  }

  override async generateQrCode(payload: string): Promise<string> {
    return this.activity.track(() =>
      QRCode.toDataURL(payload, {
        margin: 1,
        width: 320
      })
    );
  }

  private async get<T>(path: string, params?: HttpParams): Promise<T> {
    return this.request(this.http.get<T>(this.url(path), { params }));
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    return this.request(this.http.post<T>(this.url(path), body));
  }

  private async put<T>(path: string, body: unknown): Promise<T> {
    return this.request(this.http.put<T>(this.url(path), body));
  }

  private async delete<T>(path: string): Promise<T> {
    return this.request(this.http.delete<T>(this.url(path)));
  }

  private buildTenantPayload(draft: TenantDraft): Record<string, string | number> {
    const payload: Record<string, string | number> = {
      name: draft.name.trim()
    };

    if (draft.brand?.trim()) {
      payload['brand'] = draft.brand.trim();
    }

    if (draft.sla?.trim()) {
      payload['sla'] = draft.sla.trim();
    }

    if (typeof draft.dpskCoverage === 'number') {
      payload['dpskCoverage'] = draft.dpskCoverage;
    }

    return payload;
  }

  private buildControllerVendorPayload(
    draft: CreateControllerVendorRequest | UpdateControllerVendorRequest | ControllerVendorDraft
  ): CreateControllerVendorRequest | UpdateControllerVendorRequest {
    return createControllerVendorRequestSchema.parse({
      name: draft.name.trim(),
      fields: this.normalizeVendorFields(draft.fields)
    });
  }

  private buildPmsVendorPayload(
    draft: CreatePmsVendorRequest | UpdatePmsVendorRequest | PmsVendorDraft
  ): CreatePmsVendorRequest | UpdatePmsVendorRequest {
    return createPmsVendorRequestSchema.parse({
      name: draft.name.trim(),
      fields: this.normalizeVendorFields(draft.fields)
    });
  }

  private buildLocationPayload(
    draft: CreateLocationRequest | UpdateLocationRequest
  ): CreateLocationRequest | UpdateLocationRequest {
    const { pms, controller, ...rest } = createLocationRequestSchema.parse(draft);

    return {
      ...rest,
      ...(pms ? { pms } : {}),
      ...(controller ? { controller } : {})
    };
  }

  private normalizeVendorFields(
    fields: ControllerVendorDraft['fields'] | PmsVendorDraft['fields']
  ): Record<string, ControllerVendorDraft['fields'][string]> {
    return Object.fromEntries(
      Object.entries(fields)
        .map(([key, value]) => [key.trim(), value] as const)
        .filter(([key]) => !!key)
    );
  }

  private async request<T>(request$: Observable<T>): Promise<T> {
    return this.activity.track(() => firstValueFrom(request$));
  }

  private toHttpParams(
    values: Record<string, string | number | boolean | null | undefined>
  ): HttpParams {
    let params = new HttpParams();

    for (const [key, value] of Object.entries(values)) {
      if (value === undefined || value === null) {
        continue;
      }

      params = params.set(key, String(value));
    }

    return params;
  }

  private url(path: string): string {
    const baseUrl = this.environment.apiBaseUrl.replace(/\/+$/, '');
    const suffix = path.startsWith('/') ? path : `/${path}`;

    return `${baseUrl}${suffix}`;
  }
}
